<template>
  <el-table
    v-loading="loading"
    :data="list"
    :maxHeight="tableHeight"
    border
    style="width: 100%"
    default-expand-all
    :cell-style="{ textAlign: 'center' }"
    :header-cell-style="{ textAlign: 'center' }"
    @sort-change="$listeners.handleSortChange || null"
    :default-sort="defaultSort || {}"
  >
    <el-table-column
      v-for="(column, index) in tableColumns"
      :key="column.key || `col_${index}`"
      :label="getLabel(column)"
      :prop="(column && column.config && column.config.prop) || ''"
      :width="(column && column.config && column.config.width) || ''"
      :sortable="
        (column &&
          column.config &&
          column.config.sortable &&
          column.config.sortable === 'custom') ||
        false
      "
    >
      <template #default="scope">
        <!-- 使用统一的单元格渲染方法 -->
        <component :is="renderCell(column.config, scope)" />
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import BaseComponent from "./BaseComponent";
import { formatDate } from "@/utils";

export default {
  name: "TableComponent",
  data() {
    return {};
  },
  mixins: [BaseComponent],
  props: {
    tableColumns: {
      type: Array,
      required: true,
    },
    list: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    tableHeight: {
      type: [String, Number],
      default: "auto",
    },
    parentCtx: {
      type: Object,
      required: true,
    },
    defaultSort: {
      type: Object,
    },
  },
  methods: {
    renderCell(config, scope) {
      if (!config || !config.type) {
        console.warn("无效的列配置:", config);
        return this.renderDefault();
      }

      const renderName = `render${
        config.type.charAt(0).toUpperCase() + config.type.slice(1)
      }`;
      const renderFunc = this[renderName];

      if (!renderFunc || typeof renderFunc !== "function") {
        console.warn(`未实现的单元格类型: ${config.type}`, config);
        return this.renderDefault(config, scope);
      }

      return renderFunc(config, scope);
    },

    // 通用单元格渲染方法
    renderBaseCell(config, scope, content) {
      const self = this;
      return {
        render: (h) =>
          h(
            "p",
            {
              key: (config.key || `col_${scope.$index}`) + "_" + scope.$index,
              style:
                config.style ||
                self.columnStyling(
                  scope.row,
                  config.styleFormatter,
                  config.prop
                ),
              on: self.bindEvents(config, scope),
            },
            content ||
              self.columnFormatter(scope.row, config.formatter, config.prop)
          ),
      };
    },

    // 默认单元格渲染
    renderDefault(config, scope) {
      return this.renderBaseCell(config, scope);
    },

    // 索引单元格渲染
    renderIndex(config, scope) {
      return {
        render: (h) =>
          h(
            "p",
            {
              style: config.style || {},
            },
            scope.$index + 1
          ),
      };
    },

    // 文本/数字/自定义/时间单元格统一渲染
    renderText(config, scope) {
      return this.renderBaseCell(config, scope);
    },
    renderNumber(config, scope) {
      return this.renderBaseCell(config, scope);
    },
    renderCustom(config, scope) {
      return this.renderBaseCell(config, scope);
    },
    renderTime(config, scope) {
      return this.renderBaseCell(config, scope);
    },

    // 标签单元格渲染
    renderTag(config, scope) {
      const { tag, name } = this.getStatusInfo(scope.row.status);
      return {
        render: (h) =>
          h(
            "el-tag",
            {
              key: config.key + "_" + scope.$index,
              attrs: { type: tag, effect: "dark", disableTransitions: true },
            },
            name
          ),
      };
    },

    // 图像单元格渲染
    renderImage(config, scope) {
      const self = this;
      const imageConfig = config.imageConfig || {};
      return {
        render: (h) =>
          h("el-image", {
            props: {
              key: config.key + "_" + scope.$index,
              src: self.buildImageUrl(scope.row, imageConfig, "src"),
              previewSrcList: [
                self.buildImageUrl(scope.row, imageConfig, "preview"),
              ],
              fit: imageConfig.fit || "scale-down",
              lazy: true,
            },
            style: {
              width: imageConfig.width || "40px",
              height: imageConfig.height || "40px",
            },
          }),
      };
    },

    // 链接单元格渲染
    renderLink(config, scope) {
      const self = this;
      return {
        render: (h) =>
          h(
            "el-link",
            {
              props: {
                key: config.key + "_" + scope.$index,
              },
              attrs: {
                underline: config.linkConfig && config.linkConfig.underline,
                type: config.linkConfig && config.linkConfig.type,
                disabled: config.linkConfig && config.linkConfig.disabled,
              },
              on: self.bindEvents(config, scope),
            },
            self.columnFormatter(scope.row, config.formatter, config.prop)
          ),
      };
    },

    // 操作按钮单元格渲染
    renderActions(config, scope) {
      const self = this;
      return {
        render: (h) =>
          h(
            "div",
            {
              key: config.key + "_" + scope.$index,
              staticClass: "action-buttons",
              style: { display: "flex", gap: "8px", justifyContent: "center" },
            },
            config.actions.map((action) =>
              this.$createElement(
                "el-button",
                {
                  props: {
                    type: "text",
                    round: action.round,
                    size: "medium",
                  },
                  style: { color: action.color },
                  on: self.bindEvents(action, scope),
                },
                action.text
              )
            )
          ),
      };
    },

    // 处理状态
    getStatusInfo(status) {
      return this.handleStatusMapping(status, this.parentCtx.statusMap);
    },

    // 列表格内容格式化
    columnFormatter(row, formatter, prop = null) {
      try {
        // 取值
        let columnValue;
        if (typeof prop === "string" && prop !== "") {
          columnValue = this.getNestedValue(row, prop);
          columnValue = this.sanitizeValue(columnValue);
        }

        // 函数处理
        if (typeof formatter === "string" && formatter !== "") {
          const payload = columnValue === "-" ? row : columnValue;
          const func = this.findFormatterFunction(formatter, this.parentCtx);

          if (func) {
            return func(payload);
          }
          console.warn(`未找到匹配的格式化函数: ${formatter}`);
        }
        return columnValue;
      } catch (error) {
        console.error("列格式化失败:", error);
        return "-";
      }
    },

    // 列表格样式格式化
    columnStyling(row, formatter, prop = null) {
      try {
        // 取值
        let columnValue;
        if (typeof prop === "string" && prop !== "") {
          columnValue = this.getNestedValue(row, prop);
          columnValue = this.sanitizeValue(columnValue);
        }

        if (!formatter || formatter === "") {
          return { fontWeight: "bold" };
        }

        // 函数处理
        if (typeof formatter === "string" && formatter !== "") {
          const payload = columnValue === "-" ? row : columnValue;
          const func = this.findFormatterFunction(formatter, this.parentCtx);

          if (func) {
            return func(payload);
          }
          console.warn(`未找到匹配的格式化函数: ${formatter}`);
        }

        return columnValue;
      } catch (error) {
        console.error("列格式化失败:", error);
        return "";
      }
    },

    // 函数查找逻辑
    findFormatterFunction(formatter, context) {
      if (context && typeof context[formatter] === "function") {
        return context[formatter];
      }
      if (this[formatter] && typeof this[formatter] === "function") {
        return this[formatter];
      }
      if (this.$common && typeof this.$common[formatter] === "function") {
        return this.$common[formatter];
      }
      // 特殊处理：如果formatter为'formatDate'，返回formatDate函数
      if (formatter === "formatDate") {
        return formatDate;
      }
      return null;
    },
  },
};
</script>
